import { ref, onUnmounted, watch } from 'vue'
import { fetchSession } from '~/composables/api'

// Refresh interval: 10 minutes (600,000 ms)
const REFRESH_INTERVAL = 10 * 60 * 1000

/**
 * Throttle for visibility-driven refreshes — skip if a refresh ran within
 * this many ms. Prevents API spam when the user toggles tabs rapidly
 * (alt+tab, dev tools refocus, etc.).
 */
const VISIBILITY_THROTTLE_MS = 30 * 1000

// Module-level state to prevent multiple intervals / listeners across
// `useAuthRefresh()` instances. Both the interval timer and the
// visibilitychange listener are singletons — startX/stopX are no-ops
// when called twice in a row.
let refreshInterval: ReturnType<typeof setInterval> | null = null
let visibilityHandler: (() => void) | null = null
const isRefreshing = ref(false)
const lastRefresh = ref<Date | null>(null)

/**
 * Composable for silent session keepalive.
 *
 * Two refresh triggers:
 *   1. Periodic (every 10 min) — prevents the backend Redis session from
 *      expiring due to inactivity (30 min timeout) while the user has the
 *      tab open but is not interacting with it.
 *   2. Tab visibility (on `visibilitychange` → 'visible') — fires immediately
 *      when the user returns to a tab that was hidden, so a session that
 *      expired in the background is detected BEFORE the user clicks
 *      anything. This makes the "old tab" UX seamless: the login modal
 *      appears the moment the user comes back, not after a failed action.
 *
 * Both triggers go through the same `silentRefresh()` → `/auth-session`
 * → backend `fastAuthenticate({ refreshSession: true })` path. A 401 is
 * caught by the global $fetch wrapper, which runs `handle401Logout()`.
 *
 * Does NOT do an initial refresh on mount — `auth.client.ts` plugin
 * already validates the session on page load.
 *
 * Usage: call `useAuthRefresh()` in app.vue.
 */
export function useAuthRefresh() {
  const { getAccessToken } = useSupabaseAuth()
  const userStore = useUserStore()

  async function silentRefresh(): Promise<boolean> {
    if (!userStore.isAuthenticated) {
      return false
    }

    isRefreshing.value = true

    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        return false
      }

      await fetchSession(accessToken)

      lastRefresh.value = new Date()
      return true
    } catch (error) {
      console.error('[useAuthRefresh] Silent refresh failed:', error)
      return false
    } finally {
      isRefreshing.value = false
    }
  }

  function startRefreshInterval(): void {
    stopRefreshInterval()

    if (!userStore.isAuthenticated) {
      return
    }

    // Only set up the interval — no immediate call.
    // auth.client.ts already validated the session on page load.
    refreshInterval = setInterval(() => {
      silentRefresh().catch((error) => {
        console.error('[useAuthRefresh] Interval refresh failed:', error)
      })
    }, REFRESH_INTERVAL)
  }

  function stopRefreshInterval(): void {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }

  /**
   * Register the `visibilitychange` listener (idempotent — singleton).
   *
   * The handler closure captures the CURRENT `silentRefresh` from the
   * `useAuthRefresh()` instance that registered it. If a later instance
   * needs its own closure, it must call `stopVisibilityListener()` first;
   * the watcher below does this automatically by un-registering on
   * `isAuthenticated → false` and re-registering on `→ true`.
   */
  function startVisibilityListener(): void {
    if (typeof document === 'undefined') return // SSR no-op
    if (visibilityHandler) return // Already registered

    visibilityHandler = () => {
      // Only fire when becoming visible — ignore the hidden transition.
      if (document.visibilityState !== 'visible') return
      // Skip if logged out by the time the event fires.
      if (!userStore.isAuthenticated) return
      // Throttle: skip if a successful refresh ran in the last
      // VISIBILITY_THROTTLE_MS. `lastRefresh` is module-level so it
      // covers refreshes from the periodic interval too.
      if (lastRefresh.value) {
        const sinceLastMs = Date.now() - lastRefresh.value.getTime()
        if (sinceLastMs < VISIBILITY_THROTTLE_MS) return
      }
      silentRefresh().catch((error) => {
        console.error('[useAuthRefresh] Visibility refresh failed:', error)
      })
    }

    document.addEventListener('visibilitychange', visibilityHandler)
  }

  function stopVisibilityListener(): void {
    if (typeof document === 'undefined') return
    if (!visibilityHandler) return
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }

  // Single watcher handles start/stop for BOTH the periodic interval and
  // the visibility listener — no onMounted needed.
  watch(
    () => userStore.isAuthenticated,
    (authenticated) => {
      if (authenticated) {
        startRefreshInterval()
        startVisibilityListener()
      } else {
        stopRefreshInterval()
        stopVisibilityListener()
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopRefreshInterval()
    stopVisibilityListener()
  })

  return {
    isRefreshing,
    lastRefresh,
    silentRefresh,
    startRefreshInterval,
    stopRefreshInterval,
    startVisibilityListener,
    stopVisibilityListener,
  }
}

/**
 * Internal helper for tests to fully reset module-level state between
 * test cases. happy-dom keeps a single `document` instance across the
 * whole vitest worker, so a `visibilitychange` listener registered by
 * one test will leak into the next unless we explicitly tear it down
 * BEFORE `vi.resetModules()` creates a fresh module copy.
 *
 * Not exported from the public composable API. Test files import this
 * directly via `import { _resetForTesting } from '../useAuthRefresh'`.
 */
export function _resetForTesting(): void {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
  if (typeof document !== 'undefined' && visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }
  isRefreshing.value = false
  lastRefresh.value = null
}
